/* eslint-disable react-hooks/exhaustive-deps, no-unused-vars */
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FileText, Download, Send, Eye, Filter, Calendar, Plus, Trash2, Search, X, Clock, CheckCircle, AlertCircle } from 'lucide-react';
import invoiceService from '../services/invoiceService';
import './InvoiceList.css';

const InvoiceList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    workerSearch: '',
    startDate: '',
    endDate: '',
    status: ''
  });

  useEffect(() => {
    loadInvoices();
  }, []);

  // Check for invoice number in URL query params
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const invoiceNumber = searchParams.get('invoiceNumber');
    
    if (invoiceNumber) {
      setFilters(prev => ({ ...prev, workerSearch: invoiceNumber }));
    }
  }, [location.search]);

  const loadInvoices = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await invoiceService.getInvoices({});
      if (response.success) {
        setInvoices(response.invoices || []);
      }
    } catch (err) {
      setError('Errore nel caricamento delle fatture: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPdf = async (invoice) => {
    try {
      // Download PDF as blob
      const blob = await invoiceService.downloadPdf(invoice.id);
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice-${invoice.invoiceNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Errore download PDF:', err);
      alert('Errore download PDF: ' + err.message);
    }
  };

  const handleSendToAcube = async (invoice) => {
    if (!window.confirm(`Inviare fattura ${invoice.invoiceNumber} ad Acube?`)) {
      return;
    }

    try {
      const response = await invoiceService.sendToAcube(invoice.id);
      if (response.success) {
        alert(`Fattura inviata con successo! ID Acube: ${response.acubeInvoiceId}`);
        loadInvoices(); // Reload to get updated status
      }
    } catch (err) {
      alert('Errore invio ad Acube: ' + err.message);
    }
  };

  const handleViewDetails = (invoice) => {
    navigate(`/admin/invoices/${invoice.id}`);
  };

  const handleDeleteInvoice = async (invoice) => {
    const warningMsg = invoice.acubeInvoiceId 
      ? `⚠️ ATTENZIONE!\n\nSei sicuro di voler eliminare la fattura ${invoice.invoiceNumber}?\n\n` +
        `Questa fattura è stata INVIATA AD ACUBE (ID: ${invoice.acubeInvoiceId}).\n` +
        `Verrà eliminata anche da Acube.\n\n` +
        `Questo rilascerà ${invoice.transactionCount} transazioni che torneranno disponibili.\n\n` +
        `Questa azione NON è reversibile!`
      : `⚠️ ATTENZIONE!\n\nSei sicuro di voler eliminare la fattura ${invoice.invoiceNumber}?\n\n` +
        `Questo rilascerà ${invoice.transactionCount} transazioni che torneranno disponibili per essere fatturate nuovamente.\n\n` +
        `Questa azione NON è reversibile!`;
    
    if (!window.confirm(warningMsg)) {
      return;
    }

    try {
      setLoading(true);
      const response = await invoiceService.deleteInvoice(invoice.id);
      if (response.success) {
        let message = `✅ Fattura eliminata con successo!\n${response.transfersReleased} transazioni rilasciate.`;
        
        // Add Acube info/warning if present
        if (response.warning) {
          message += `\n\n⚠️ ${response.warning}`;
        } else if (response.acube && response.acube.deleted) {
          message += `\n✅ Eliminata anche da Acube`;
        }
        
        alert(message);
        loadInvoices(); // Reload list
      }
    } catch (err) {
      alert('❌ Errore eliminazione fattura: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (cents) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(cents / 100);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('it-IT', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadge = (invoice) => {
    const statusConfig = {
      sent: { icon: CheckCircle, label: 'Inviata', color: '#4caf50', bg: '#e8f5e9' },
      delivered: { icon: CheckCircle, label: 'Consegnata', color: '#20c997', bg: '#e6fcf5' },
      'not-delivered': { icon: CheckCircle, label: 'Messa a disposizione', color: '#fd7e14', bg: '#fff3e0' },
      pending: { icon: Clock, label: 'In Attesa', color: '#ff9800', bg: '#fff3e0' },
      rejected: { icon: AlertCircle, label: 'Rifiutata', color: '#f44336', bg: '#ffebee' },
      error: { icon: AlertCircle, label: 'Errore', color: '#f44336', bg: '#ffebee' }
    };

    let status = 'pending';
    if (invoice.acubeInvoiceStatus === 'sent') status = 'sent';
    else if (invoice.acubeInvoiceStatus === 'delivered') status = 'delivered';
    else if (invoice.acubeInvoiceStatus === 'not-delivered' || invoice.acubeInvoiceStatus === 'not_delivered' || invoice.acubeInvoiceStatus === 'impossible_to_deliver') status = 'not-delivered';
    else if (invoice.acubeInvoiceStatus === 'rejected') status = 'rejected';
    else if (invoice.acubeInvoiceStatus === 'error') status = 'error';

    const config = statusConfig[status];
    const Icon = config.icon;

    return (
      <span className="status-badge" style={{ color: config.color, background: config.bg }}>
        <Icon size={14} />
        {config.label}
      </span>
    );
  };

  const filteredInvoices = invoices.filter(invoice => {
    if (filters.status) {
      const currentStatus = invoice.acubeInvoiceStatus === 'sent' ? 'sent' : 
                           invoice.acubeInvoiceStatus === 'error' ? 'error' : 'pending';
      if (currentStatus !== filters.status) return false;
    }
    if (filters.workerSearch) {
      const search = filters.workerSearch.toLowerCase();
      const matchNumber = invoice.invoiceNumber?.toLowerCase().includes(search);
      const matchEmail = invoice.workerPaymentProfile?.user?.email?.toLowerCase().includes(search);
      if (!matchNumber && !matchEmail) return false;
    }
    if (filters.startDate && new Date(invoice.generatedAt) < new Date(filters.startDate)) return false;
    if (filters.endDate && new Date(invoice.generatedAt) > new Date(filters.endDate)) return false;
    return true;
  });

  if (loading) {
    return (
      <div className="invoice-list-page">
        <div className="loading-state">
          <Clock size={48} />
          <p>Caricamento fatture...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="invoice-list-page">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1>
            <FileText size={32} />
            Fatture Percettori
          </h1>
          <p>Visualizza e gestisci le fatture generate</p>
        </div>
        <div className="header-actions">
          <button 
            onClick={() => navigate('/admin/invoices/generate')} 
            className="btn-generate-invoice"
          >
            <Plus size={18} />
            Genera Nuova Fattura
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <span className="stat-value">{invoices.length}</span>
          <span className="stat-label">Totale</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">
            {invoices.filter(i => i.acubeInvoiceStatus === 'sent').length}
          </span>
          <span className="stat-label">Inviate</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">
            {invoices.filter(i => !i.acubeInvoiceStatus || i.acubeInvoiceStatus === 'pending').length}
          </span>
          <span className="stat-label">In Attesa</span>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-card">
        <h2><Filter size={20} /> Filtri</h2>
        <div className="filters-grid">
          <div className="filter-group">
            <label><Search size={16} /> Cerca</label>
            <input
              type="text"
              placeholder="Numero fattura o email percettore"
              value={filters.workerSearch}
              onChange={(e) => setFilters({ ...filters, workerSearch: e.target.value })}
            />
          </div>
          <div className="filter-group">
            <label>Stato</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            >
              <option value="">Tutti</option>
              <option value="sent">Inviate</option>
              <option value="pending">In Attesa</option>
              <option value="error">Errore</option>
            </select>
          </div>
          <div className="filter-group">
            <label>Da Data</label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
            />
          </div>
          <div className="filter-group">
            <label>A Data</label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
            />
          </div>
        </div>
        {(filters.status || filters.workerSearch || filters.startDate || filters.endDate) && (
          <button
            className="clear-filters-btn"
            onClick={() => setFilters({ workerSearch: '', status: '', startDate: '', endDate: '' })}
          >
            Reset Filtri
          </button>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="alert alert-error">
          <AlertCircle size={20} />
          {error}
        </div>
      )}

      {/* Invoices List */}
      {filteredInvoices.length === 0 ? (
        <div className="empty-state">
          <FileText size={64} />
          <h3>Nessuna fattura trovata</h3>
          <p>
            {invoices.length === 0
              ? 'Non ci sono ancora fatture generate.'
              : 'Nessun risultato con i filtri selezionati.'}
          </p>
        </div>
      ) : (
        <div className="invoices-table-wrapper">
          <table className="invoices-table">
            <thead>
              <tr>
                <th className="col-number">Numero</th>
                <th className="col-date hide-mobile">Data</th>
                <th className="col-worker">Percettore</th>
                <th className="col-period hide-tablet">Periodo</th>
                <th className="col-transactions text-center hide-mobile">Trans.</th>
                <th className="col-amount-gross text-right hide-mobile">Lordo</th>
                <th className="col-amount-fee text-right">Fattura</th>
                <th className="col-status">Stato</th>
                <th className="col-actions text-center">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {filteredInvoices.map((invoice) => (
                <tr key={invoice.id}>
                  <td className="col-number invoice-number">
                    <div className="cell-content">
                      <strong>{invoice.invoiceNumber}</strong>
                      <div className="mobile-only mobile-info">
                        <small>{formatDate(invoice.generatedAt)}</small>
                      </div>
                    </div>
                  </td>
                  <td className="col-date hide-mobile">
                    {invoice.generatedAt ? formatDate(invoice.generatedAt) : '-'}
                  </td>
                  <td className="col-worker">
                    <div className="worker-info">
                      <div className="worker-email">{invoice.workerPaymentProfile?.user?.email || 'N/A'}</div>
                      <div className="mobile-only mobile-period">
                        <small>📅 {formatDate(invoice.periodStart)} - {formatDate(invoice.periodEnd)}</small>
                      </div>
                    </div>
                  </td>
                  <td className="col-period hide-tablet">
                    <div className="period-cell">
                      <Calendar size={14} />
                      {formatDate(invoice.periodStart)} - {formatDate(invoice.periodEnd)}
                    </div>
                  </td>
                  <td className="col-transactions text-center hide-mobile">
                    <span className="transaction-count">{invoice.transactionCount}</span>
                  </td>
                  <td className="col-amount-gross text-right hide-mobile">
                    {formatCurrency(invoice.totalGross)}
                  </td>
                  <td className="col-amount-fee text-right amount-cell">
                    <strong>{formatCurrency(invoice.totalFeeTotal)}</strong>
                    <div className="mobile-only mobile-transactions">
                      <small>{invoice.transactionCount} trans.</small>
                    </div>
                  </td>
                  <td className="col-status">{getStatusBadge(invoice)}</td>
                  <td className="col-actions text-center">
                    <div className="action-buttons">
                      <button
                        onClick={() => handleViewDetails(invoice)}
                        className="action-btn view-btn"
                        title="Visualizza dettagli"
                      >
                        <Eye size={16} />
                      </button>
                      {invoice.acubeInvoiceId ? (
                        <button
                          onClick={() => handleDownloadPdf(invoice)}
                          className="action-btn download-btn"
                          title="Scarica PDF"
                        >
                          <Download size={16} />
                        </button>
                      ) : (
                        <button
                          onClick={() => handleSendToAcube(invoice)}
                          className="action-btn send-btn"
                          title="Invia ad Acube"
                        >
                          <Send size={16} />
                        </button>
                      )}
                      <button
                        onClick={() => handleDeleteInvoice(invoice)}
                        className="action-btn delete-btn"
                        title="Elimina fattura"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default InvoiceList;
